import {API} from "@/app/api/BaseAPI";

/**
 * A class for calling UltiPanel WebSocket API.
 *
 * This API provides comprehensive server monitoring, plugin management, and real-time communication features.
 * All API endpoints require user authentication with Bearer Token.
 *
 * @class PluginAPI
 * @author qianmo
 * @version 2.0.0
 * @since 2.0.0
 */
export class PluginAPI {
  /**
   * WebSocket service status endpoint
   */
  readonly wsStatusPath = "/ws/status"

  /**
   * Base WebSocket server endpoint
   */
  readonly wsServerBasePath = "/ws/server"

  /**
   * Get WebSocket service status
   *
   * @returns Promise of WebSocket service status
   */
  getWebSocketStatus(): Promise<any> {
    const api = new API();

    return new Promise((resolve, reject) => {
      api
        .withPath(this.wsStatusPath)
        .withMethod("GET")
        .withToken()
        .request()
        .then((response) => {
          resolve(response.data);
        }, (reason) => {
          reject(reason);
        });
    });
  }

  /**
   * Get server status (cached)
   *
   * @param serverId - Server UUID
   * @returns Promise of server status
   */
  getServerStatus(serverId: string): Promise<any> {
    const api = new API();

    return new Promise((resolve, reject) => {
      api
        .withPath(`${this.wsServerBasePath}/${serverId}/status`)
        .withMethod("GET")
        .withToken()
        .request()
        .then((response) => {
          resolve(response.data);
        }, (reason) => {
          reject(reason);
        });
    });
  }

  /**
   * Request server status update
   *
   * @param serverId - Server UUID
   * @param requestId - Optional request ID
   * @returns Promise of response
   */
  requestServerStatus(serverId: string, requestId?: string): Promise<any> {
    const api = new API();
    const body: any = {};
    if (requestId) body.requestId = requestId;

    return new Promise((resolve, reject) => {
      api
        .withPath(`${this.wsServerBasePath}/${serverId}/status/request`)
        .withBody(body)
        .withMethod("POST")
        .withToken()
        .request()
        .then((response) => {
          resolve(response.data);
        }, (reason) => {
          reject(reason);
        });
    });
  }

  /**
   * Get plugin list (cached)
   *
   * @param serverId - Server UUID
   * @returns Promise of plugin list
   */
  getPluginList(serverId: string): Promise<any> {
    const api = new API();

    return new Promise((resolve, reject) => {
      api
        .withPath(`${this.wsServerBasePath}/${serverId}/plugins`)
        .withMethod("GET")
        .withToken()
        .request()
        .then((response) => {
          resolve(response.data);
        }, (reason) => {
          reject(reason);
        });
    });
  }

  /**
   * Request plugin list update
   *
   * @param serverId - Server UUID
   * @returns Promise of response
   */
  requestPluginList(serverId: string): Promise<any> {
    const api = new API();

    return new Promise((resolve, reject) => {
      api
        .withPath(`${this.wsServerBasePath}/${serverId}/plugins/request`)
        .withMethod("POST")
        .withToken()
        .request()
        .then((response) => {
          resolve(response.data);
        }, (reason) => {
          reject(reason);
        });
    });
  }

  /**
   * Get player events
   *
   * @param serverId - Server UUID
   * @param limit - Optional: number of events to return, default 50
   * @returns Promise of player events
   */
  getPlayerEvents(serverId: string, limit?: number): Promise<any> {
    const api = new API();

    return new Promise((resolve, reject) => {
      api
        .withPath(`${this.wsServerBasePath}/${serverId}/events`)
        .withParams(limit ? { limit } : {})
        .withMethod("GET")
        .withToken()
        .request()
        .then((response) => {
          resolve(response.data);
        }, (reason) => {
          reject(reason);
        });
    });
  }

  /**
   * Get server metrics (cached)
   *
   * @param serverId - Server UUID
   * @returns Promise of server metrics
   */
  getServerMetrics(serverId: string): Promise<any> {
    const api = new API();

    return new Promise((resolve, reject) => {
      api
        .withPath(`${this.wsServerBasePath}/${serverId}/metrics`)
        .withMethod("GET")
        .withToken()
        .request()
        .then((response) => {
          resolve(response.data);
        }, (reason) => {
          reject(reason);
        });
    });
  }

  /**
   * Request server metrics update
   *
   * @param serverId - Server UUID
   * @returns Promise of response
   */
  requestMetrics(serverId: string): Promise<any> {
    const api = new API();

    return new Promise((resolve, reject) => {
      api
        .withPath(`${this.wsServerBasePath}/${serverId}/metrics/request`)
        .withMethod("POST")
        .withToken()
        .request()
        .then((response) => {
          resolve(response.data);
        }, (reason) => {
          reject(reason);
        });
    });
  }

  /**
   * Get server logs
   *
   * @param serverId - Server UUID
   * @param limit - Optional: number of log entries to return, default 100
   * @returns Promise of server logs
   */
  getServerLogs(serverId: string, limit?: number): Promise<any> {
    const api = new API();

    return new Promise((resolve, reject) => {
      api
        .withPath(`${this.wsServerBasePath}/${serverId}/logs`)
        .withParams(limit ? { limit } : {})
        .withMethod("GET")
        .withToken()
        .request()
        .then((response) => {
          resolve(response.data);
        }, (reason) => {
          reject(reason);
        });
    });
  }

  /**
   * Control log stream
   *
   * @param serverId - Server UUID
   * @param action - Action type: start|stop|pause|resume
   * @param level - Optional: log level: info|warning|error|debug
   * @returns Promise of response
   */
  controlLogStream(serverId: string, action: 'start'|'stop'|'pause'|'resume',
                   level?: 'info'|'warning'|'error'|'debug'): Promise<any> {
    const api = new API();
    const body: any = { action };
    if (level !== undefined) body.level = level;

    return new Promise((resolve, reject) => {
      api
        .withPath(`${this.wsServerBasePath}/${serverId}/log/stream`)
        .withBody(body)
        .withMethod("POST")
        .withToken()
        .request()
        .then((response) => {
          resolve(response.data);
        }, (reason) => {
          reject(reason);
        });
    });
  }

  /**
   * Get command history
   *
   * @param serverId - Server UUID
   * @param limit - Optional: number of commands to return, default 20
   * @returns Promise of command history
   */
  getCommandHistory(serverId: string, limit?: number): Promise<any> {
    const api = new API();

    return new Promise((resolve, reject) => {
      api
        .withPath(`${this.wsServerBasePath}/${serverId}/commands`)
        .withParams(limit ? { limit } : {})
        .withMethod("GET")
        .withToken()
        .request()
        .then((response) => {
          resolve(response.data);
        }, (reason) => {
          reject(reason);
        });
    });
  }

  /**
   * Execute command on server
   *
   * @param serverId - Server UUID
   * @param command - Command to execute
   * @param async - Optional: whether to execute asynchronously, default true
   * @param timeout - Optional: timeout in milliseconds, default 30000
   * @returns Promise of response
   */
  executeCommand(serverId: string, command: string, async?: boolean, timeout?: number): Promise<any> {
    const api = new API();
    const body: any = { command };
    if (async !== undefined) body.async = async;
    if (timeout !== undefined) body.timeout = timeout;

    return new Promise((resolve, reject) => {
      api
        .withPath(`${this.wsServerBasePath}/${serverId}/command/execute`)
        .withBody(body)
        .withMethod("POST")
        .withToken()
        .request()
        .then((response) => {
          resolve(response.data);
        }, (reason) => {
          reject(reason);
        });
    });
  }

  /**
   * Get command execution result
   *
   * @param serverId - Server UUID
   * @param commandId - Command ID
   * @returns Promise of command result
   */
  getCommandResult(serverId: string, commandId: string): Promise<any> {
    const api = new API();

    return new Promise((resolve, reject) => {
      api
        .withPath(`${this.wsServerBasePath}/${serverId}/command/${commandId}/result`)
        .withMethod("GET")
        .withToken()
        .request()
        .then((response) => {
          resolve(response.data);
        }, (reason) => {
          reject(reason);
        });
    });
  }

  /**
   * Perform file operation
   *
   * @param serverId - Server UUID
   * @param operation - Operation type: read|write|delete|list|move|copy
   * @param path - File path
   * @param content - Optional: file content (for write operations)
   * @param recursive - Optional: whether to be recursive (for delete/list operations)
   * @returns Promise of response
   */
  fileOperation(serverId: string, operation: 'read'|'write'|'delete'|'list'|'move'|'copy',
                path: string, content?: string, recursive?: boolean): Promise<any> {
    const api = new API();
    const body: any = { operation, path };
    if (content !== undefined) body.content = content;
    if (recursive !== undefined) body.recursive = recursive;

    return new Promise((resolve, reject) => {
      api
        .withPath(`${this.wsServerBasePath}/${serverId}/file/operation`)
        .withBody(body)
        .withMethod("POST")
        .withToken()
        .request()
        .then((response) => {
          resolve(response.data);
        }, (reason) => {
          reject(reason);
        });
    });
  }

  /**
   * Get file operation status
   *
   * @param serverId - Server UUID
   * @param operationId - Operation ID
   * @returns Promise of file operation status
   */
  getFileOperationStatus(serverId: string, operationId: string): Promise<any> {
    const api = new API();

    return new Promise((resolve, reject) => {
      api
        .withPath(`${this.wsServerBasePath}/${serverId}/file-operation/${operationId}`)
        .withMethod("GET")
        .withToken()
        .request()
        .then((response) => {
          resolve(response.data);
        }, (reason) => {
          reject(reason);
        });
    });
  }

  /**
   * Execute a file operation and wait for the result by polling.
   *
   * Sends the operation request, then polls getFileOperationStatus
   * until the operation completes or times out.
   *
   * @param serverId - Server UUID
   * @param operation - Operation type
   * @param path - File path
   * @param content - Optional: file content (for write)
   * @param recursive - Optional: recursive flag
   * @param maxWaitMs - Max wait time in ms (default 15000)
   * @param pollIntervalMs - Poll interval in ms (default 500)
   * @returns Promise of completed operation data
   */
  async fileOperationAndWait(
    serverId: string,
    operation: 'read' | 'write' | 'delete' | 'list' | 'move' | 'copy',
    path: string,
    content?: string,
    recursive?: boolean,
    maxWaitMs = 15000,
    pollIntervalMs = 500,
  ): Promise<any> {
    const postResult = await this.fileOperation(serverId, operation, path, content, recursive);
    const operationId = postResult?.operationId;
    if (!operationId) {
      throw new Error('No operationId returned from file operation');
    }

    const deadline = Date.now() + maxWaitMs;
    while (Date.now() < deadline) {
      await new Promise(r => setTimeout(r, pollIntervalMs));
      const status = await this.getFileOperationStatus(serverId, operationId);
      if (status?.operation?.status === 'completed') {
        return status.operation;
      }
      if (status?.operation?.status === 'error') {
        throw new Error(status.operation.error || 'File operation failed');
      }
    }
    throw new Error(`File operation timed out after ${maxWaitMs}ms`);
  }

  /**
   * Get server configuration
   *
   * @param serverId - Server UUID
   * @param configType - Configuration type
   * @returns Promise of server configuration
   */
  getServerConfig(serverId: string, configType: string): Promise<any> {
    const api = new API();

    return new Promise((resolve, reject) => {
      api
        .withPath(`${this.wsServerBasePath}/${serverId}/config/${configType}`)
        .withMethod("GET")
        .withToken()
        .request()
        .then((response) => {
          resolve(response.data);
        }, (reason) => {
          reject(reason);
        });
    });
  }

  /**
   * Get configuration list
   *
   * @param serverId - Server UUID
   * @returns Promise of configuration list
   */
  getConfigList(serverId: string): Promise<any> {
    const api = new API();

    return new Promise((resolve, reject) => {
      api
        .withPath(`${this.wsServerBasePath}/${serverId}/configs`)
        .withMethod("GET")
        .withToken()
        .request()
        .then((response) => {
          resolve(response.data);
        }, (reason) => {
          reject(reason);
        });
    });
  }

  /**
   * Update server configuration
   *
   * @param serverId - Server UUID
   * @param configData - Configuration data
   * @param fileName - Optional: configuration file name
   * @returns Promise of response
   */
  updateConfig(serverId: string, configData: string, fileName?: string): Promise<any> {
    const api = new API();
    const body: any = { configData };
    if (fileName) body.fileName = fileName;

    return new Promise((resolve, reject) => {
      api
        .withPath(`${this.wsServerBasePath}/${serverId}/config/update`)
        .withBody(body)
        .withMethod("POST")
        .withToken()
        .request()
        .then((response) => {
          resolve(response.data);
        }, (reason) => {
          reject(reason);
        });
    });
  }

  /**
   * Get backup list
   *
   * @param serverId - Server UUID
   * @returns Promise of backup list
   */
  getBackupList(serverId: string): Promise<any> {
    const api = new API();

    return new Promise((resolve, reject) => {
      api
        .withPath(`${this.wsServerBasePath}/${serverId}/backups`)
        .withMethod("GET")
        .withToken()
        .request()
        .then((response) => {
          resolve(response.data);
        }, (reason) => {
          reject(reason);
        });
    });
  }

  /**
   * Get server metrics history (time-series data from D1)
   *
   * @param serverId - Server UUID
   * @param start - Optional: start timestamp in seconds (default: 24h ago)
   * @param end - Optional: end timestamp in seconds (default: now)
   * @param limit - Optional: max data points to return (default: 1440)
   * @returns Promise of metrics history
   */
  getMetricsHistory(serverId: string, start?: number, end?: number, limit?: number): Promise<any> {
    const api = new API();
    const params: Record<string, any> = {};
    if (start !== undefined) params.start = start;
    if (end !== undefined) params.end = end;
    if (limit !== undefined) params.limit = limit;

    return new Promise((resolve, reject) => {
      api
        .withPath(`${this.wsServerBasePath}/${serverId}/metrics/history`)
        .withParams(params)
        .withMethod("GET")
        .withToken()
        .request()
        .then((response) => {
          resolve(response.data);
        }, (reason) => {
          reject(reason);
        });
    });
  }

  /**
   * Get error history for a server
   *
   * @param serverId - Server UUID
   * @param params - Optional query params: resolved, module, limit, offset
   * @returns Promise of error history
   */
  getErrorHistory(serverId: string, params?: { resolved?: boolean; module?: string; isFramework?: boolean; limit?: number; offset?: number }): Promise<any> {
    const api = new API();
    const queryParams: Record<string, any> = {};
    if (params) {
      if (params.resolved !== undefined) queryParams.resolved = params.resolved;
      if (params.module) queryParams.module = params.module;
      if (params.isFramework !== undefined) queryParams.isFramework = params.isFramework;
      if (params.limit !== undefined) queryParams.limit = params.limit;
      if (params.offset !== undefined) queryParams.offset = params.offset;
    }

    return new Promise((resolve, reject) => {
      api
        .withPath(`${this.wsServerBasePath}/${serverId}/errors/history`)
        .withParams(queryParams)
        .withMethod("GET")
        .withToken()
        .request()
        .then((response) => {
          resolve(response.data);
        }, (reason) => {
          reject(reason);
        });
    });
  }

  /**
   * Get recent errors for a server
   *
   * @param serverId - Server UUID
   * @returns Promise of recent errors
   */
  getRecentErrors(serverId: string): Promise<any> {
    const api = new API();

    return new Promise((resolve, reject) => {
      api
        .withPath(`${this.wsServerBasePath}/${serverId}/errors`)
        .withMethod("GET")
        .withToken()
        .request()
        .then((response) => {
          resolve(response.data);
        }, (reason) => {
          reject(reason);
        });
    });
  }

  /**
   * Resolve an error by fingerprint
   *
   * @param serverId - Server UUID
   * @param fingerprint - Error fingerprint
   * @returns Promise of response
   */
  resolveError(serverId: string, fingerprint: string): Promise<any> {
    const api = new API();

    return new Promise((resolve, reject) => {
      api
        .withPath(`${this.wsServerBasePath}/${serverId}/errors/${fingerprint}/resolve`)
        .withMethod("POST")
        .withToken()
        .request()
        .then((response) => {
          resolve(response.data);
        }, (reason) => {
          reject(reason);
        });
    });
  }

  /**
   * Unresolve an error by fingerprint
   *
   * @param serverId - Server UUID
   * @param fingerprint - Error fingerprint
   * @returns Promise of response
   */
  unresolveError(serverId: string, fingerprint: string): Promise<any> {
    const api = new API();

    return new Promise((resolve, reject) => {
      api
        .withPath(`${this.wsServerBasePath}/${serverId}/errors/${fingerprint}/unresolve`)
        .withMethod("POST")
        .withToken()
        .request()
        .then((response) => {
          resolve(response.data);
        }, (reason) => {
          reject(reason);
        });
    });
  }

  /**
   * Get error statistics for a server
   *
   * @param serverId - Server UUID
   * @returns Promise of error stats
   */
  getErrorStats(serverId: string): Promise<any> {
    const api = new API();

    return new Promise((resolve, reject) => {
      api
        .withPath(`${this.wsServerBasePath}/${serverId}/errors/stats`)
        .withMethod("GET")
        .withToken()
        .request()
        .then((response) => {
          resolve(response.data);
        }, (reason) => {
          reject(reason);
        });
    });
  }

  /**
   * Get backup operation status
   *
   * @param serverId - Server UUID
   * @param operationId - Operation ID
   * @returns Promise of backup operation status
   */
  getBackupOperationStatus(serverId: string, operationId: string): Promise<any> {
    const api = new API();

    return new Promise((resolve, reject) => {
      api
        .withPath(`${this.wsServerBasePath}/${serverId}/backup-operation/${operationId}`)
        .withMethod("GET")
        .withToken()
        .request()
        .then((response) => {
          resolve(response.data);
        }, (reason) => {
          reject(reason);
        });
    });
  }
}
